import { useTranslation } from 'react-i18next';

import { ReactComponent as InfoIcon } from '../../../../../../../../../assets/icons/info.svg';
import Table from '../../../../../../../../../shared/components/Globals/Table/Table';
import type { ICommercialAddon, ICensusValidationResult } from '../../../../../../../../../shared/interfaces/quotes';
import { TRANSLATIONS_QUOTES } from '../../../../../../../../../shared/translations/quotes';
import type { TobaccoStatus } from '../../../../../../../../../shared/types/quotes';
import { formatCurrency } from '../../../../../../../../../shared/utils/currency/currency';
import type { AgeBandBreakdownRow } from '../../../../../../../../../shared/utils/quotes/quotes';
import { getAddonRate } from '../../../../../../../../../shared/utils/quotes/quotes';
import './CensusSuccessStep.scss';

export interface CensusSuccessStepProps {
  rowCount: number;
  avgAge: number | null;
  ageBandBreakdown: AgeBandBreakdownRow[];
  addon?: ICommercialAddon | null;
  tobaccoStatus?: TobaccoStatus;
  productName?: string;
  summary?: ICensusValidationResult['summary'];
}

export default function CensusSuccessStep({
  rowCount,
  avgAge,
  ageBandBreakdown,
  addon,
  tobaccoStatus,
  productName,
  summary
}: Readonly<CensusSuccessStepProps>) {
  const { t } = useTranslation();

  return (
    <div className="census-step">
      <div className="census-success-sub">
        {t(TRANSLATIONS_QUOTES.create.censusModal.successSub, { count: rowCount, avgAge: avgAge ?? 0 })}
      </div>

      {ageBandBreakdown.length > 0 && (
        <div className="rate-table__scroll census-table-wrap">
          <Table density="sm">
            <Table.Head>
              <Table.Row>
                <Table.HeaderCell scope="col">{t(TRANSLATIONS_QUOTES.create.censusModal.ageBand)}</Table.HeaderCell>
                <Table.HeaderCell scope="col">{t(TRANSLATIONS_QUOTES.create.censusModal.employees)}</Table.HeaderCell>
                {addon && (
                  <>
                    <Table.HeaderCell scope="col">{t(TRANSLATIONS_QUOTES.create.censusModal.rateEe)}</Table.HeaderCell>
                    <Table.HeaderCell scope="col">
                      {t(TRANSLATIONS_QUOTES.create.censusModal.bandTotal)}
                    </Table.HeaderCell>
                  </>
                )}
              </Table.Row>
            </Table.Head>
            <Table.Body>
              {ageBandBreakdown.map((b) => {
                const ratePerEe = addon && tobaccoStatus ? getAddonRate(addon, b.ageFrom, tobaccoStatus) : null;
                const bandTotal =
                  addon && tobaccoStatus && summary
                    ? summary
                        .filter((s) => s.age >= b.ageFrom && s.age <= b.ageTo)
                        .reduce((acc, s) => acc + (getAddonRate(addon, s.age, tobaccoStatus) ?? 0) * s.quantity, 0)
                    : null;

                return (
                  <Table.Row key={`${b.ageFrom}-${b.ageTo}`}>
                    <Table.Cell>{b.ageTo >= 99 ? `${b.ageFrom}+` : `${b.ageFrom}–${b.ageTo}`}</Table.Cell>
                    <Table.Cell>{b.count}</Table.Cell>
                    {addon && (
                      <>
                        <Table.Cell>{ratePerEe == null ? '—' : formatCurrency(ratePerEe)}</Table.Cell>
                        <Table.Cell>{bandTotal == null ? '—' : formatCurrency(bandTotal)}</Table.Cell>
                      </>
                    )}
                  </Table.Row>
                );
              })}
            </Table.Body>
          </Table>
        </div>
      )}

      {addon && (
        <div className="census-callout census-callout--info">
          <InfoIcon className="census-callout__icon" aria-hidden="true" />
          <span>{t(TRANSLATIONS_QUOTES.create.censusModal.warning, { addon: productName })}</span>
        </div>
      )}
    </div>
  );
}
